import React, { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import Papa from "papaparse";
import { blue, grey, deepPurple, teal } from "@material-ui/core/colors";
import exportacoesCSV from "../utils/data/exportacoes1990_2025.csv";

const allMonths = [
  "jul",
  "ago",
  "set",
  "out",
  "nov",
  "dez",
  "jan",
  "fev",
  "mar",
  "abr",
  "mai",
  "jun",
];

const GRAY = grey[400] || "#999999";

// -------------------- CSV Parsing --------------------
function parseCSV(callback) {
  fetch(exportacoesCSV)
    .then((response) => response.text())
    .then((csvText) => {
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => callback(results.data),
      });
    });
}

// -------------------- Prepare Data --------------------
function prepareData(data, tipo, projecoes) {
  const monthlyData = {};

  data.forEach((row) => {
    const [monthStr, yearStr] = row["Mes/Ano"].split("-");
    const month = monthStr.toLowerCase().slice(0, 3);
    const year = parseInt(
      `20${yearStr.length === 2 ? yearStr : yearStr.slice(-2)}`,
    );

    const rawValue = row[tipo]?.replace(/\./g, "").replace(",", ".");
    const value = parseFloat(rawValue);
    if (isNaN(value)) return;

    if (!monthlyData[year]) monthlyData[year] = {};
    monthlyData[year][month] = value;
  });

  function getSeasonData(startYear, tipo, projecoes) {
    // Create 12-element array, NaN for missing months
    const data = allMonths.map((month, idx) => {
      const year = idx < 6 ? startYear : startYear + 1;
      const val = monthlyData[year]?.[month];
      return typeof val === "number" ? val : NaN;
    });

    // Determine which projection value to use
    let projValue = null;
    if (projecoes) {
      if (tipo === "Conillon" && projecoes.conilon != null)
        projValue = projecoes.conilon;
      if (tipo === "Arabica" && projecoes.arabica != null)
        projValue = projecoes.arabica;
    }

    if (projValue != null) {
      // Find the last index with real data
      const lastIndex =
        data.findIndex(
          (v, idx) => idx === data.length - 1 || isNaN(v) || v === null,
        ) - 1; // last valid data index
      const insertIndex = lastIndex >= 0 ? lastIndex + 1 : 0;

      if (insertIndex < data.length) {
        data[insertIndex] = projValue;
      }
    }

    return data;
  }

  return {
    "2020/21": getSeasonData(2020, tipo),
    "2021/22": getSeasonData(2021, tipo),
    "2022/23": getSeasonData(2022, tipo),
    "2023/24": getSeasonData(2023, tipo),
    "2024/25": getSeasonData(2024, tipo),
    "2025/26": getSeasonData(2025, tipo, projecoes),
  };
}

// -------------------- Build Series --------------------
function buildSeries(dataMap) {
  const colors = {
    "2020/21": "#ff7043",
    "2021/22": "#66bb6a",
    "2022/23": teal[500],
    "2023/24": blue[500],
    "2024/25": deepPurple[500],
    "2025/26": "yellow",
  };

  const yearsForAverage = [
    "2020/21",
    "2021/22",
    "2022/23",
    "2023/24",
    "2024/25",
    "2025/26",
  ];
  const averageData = allMonths.map((_, idx) => {
    const values = yearsForAverage
      .map((year) => dataMap[year][idx])
      .filter((v) => !isNaN(v));
    return values.length
      ? parseFloat(
          (values.reduce((a, b) => a + b, 0) / values.length / 1000).toFixed(1),
        )
      : NaN;
  });

  const series = Object.keys(dataMap).map((label) => ({
    name: label,
    type: "line",
    data: dataMap[label].map((v) =>
      !isNaN(v) ? parseFloat((v / 1000).toFixed(1)) : NaN,
    ),
    smooth: true,
    connectNulls: true,
    lineStyle: { width: 2 },
    color: colors[label],
    label: { show: false },
  }));

  series.push({
    name: "Média 5 Anos",
    type: "line",
    data: averageData,
    smooth: true,
    connectNulls: true,
    lineStyle: { width: 2, type: "dashed" },
    color: grey[700],
    label: { show: false },
  });

  return series;
}

// -------------------- Chart Options --------------------
function buildChartOption(title, series, yInterval, min) {
  return {
    title: { text: title, left: "center", textStyle: { color: GRAY } },
    tooltip: {
      trigger: "axis",
      valueFormatter: (value) =>
        !isNaN(value) ? `${value.toFixed(1)} mil sacas` : "",
    },
    legend: {
      top: 30,
      textStyle: { color: grey[500] },
      selected: {
        "2020/21": false,
        "2021/22": false,
        "2022/23": false,
        "2023/24": false,
        "2024/25": true,
        "2025/26": true,
        "Média 5 Anos": true,
      },
    },
    xAxis: { type: "category", data: allMonths },
    yAxis: { type: "value", name: "Mil sacas", interval: yInterval, min },
    series,
    grid: { left: "10%", right: "10%", bottom: "15%" },
    toolbox: { feature: { saveAsImage: {}, dataZoom: {}, restore: {} } },
    graphic: {
      elements: [
        {
          type: "text",
          left: "center",
          top: "center",
          style: {
            text: "cafetrade.com.br",
            fontSize: 30,
            fontWeight: "bold",
            fill: "rgba(200, 200, 200, 0.3)",
          },
          z: -10,
        },
      ],
    },
  };
}

// -------------------- React Component --------------------
export default function ExportacoesCafeCharts({ projecoes }) {
  const [conillonSeries, setConillonSeries] = useState([]);
  const [arabicaSeries, setArabicaSeries] = useState([]);

  useEffect(() => {
    parseCSV((parsedData) => {
      const conillonData = prepareData(parsedData, "Conillon", projecoes);

      const arabicaData = prepareData(parsedData, "Arabica", projecoes);

      setConillonSeries(buildSeries(conillonData));
      setArabicaSeries(buildSeries(arabicaData));
    });
  }, [projecoes]);

  return (
    <>
      <div
        style={{
          height: "700px",
          width: "100%",
          marginTop: 60,
          marginBottom: 60,
        }}
      >
        <ReactECharts
          option={buildChartOption(
            "Exportações Mensais de Arábica - Anos Safra",
            arabicaSeries,
            500,
            0,
          )}
          style={{ height: "100%" }}
        />
      </div>

      <div style={{ height: "700px", width: "100%" }}>
        <ReactECharts
          option={buildChartOption(
            "Exportações Mensais de Conillon",
            conillonSeries,
            100,
            0,
          )}
          style={{ height: "100%" }}
        />
      </div>
    </>
  );
}
